import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ReplayIcon from '@mui/icons-material/Replay';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { TaskDetailDialog, useTasks } from '@modules/background-tasks';
import { useBillingAccess } from '../hooks/useBillingAccess';
import { createBillingAccessApiService } from '../services/BillingAccessApiService';
import CreditLedgerHistoryDialog from '../components/CreditLedgerHistoryDialog';
import SubscriptionProductsList from '../components/SubscriptionProductsList';
import type { BillingCreditLedgerEntry, BillingProduct, BillingSubscriptionSummary } from '../types';

interface TaskBillingSummary {
  creditDebitMinor?: number;
  customerAmountMinor?: number;
  quotaQuantity?: number;
  creditQuantity?: number;
  currency?: string;
  provider?: string;
  model?: string;
}

function getTaskBilling(task: unknown): TaskBillingSummary | undefined {
  return (task as { billing?: TaskBillingSummary }).billing;
}

function moneyToLabel(amountMinor: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountMinor / 100);
}

function getCreditAmount(product: BillingProduct): number {
  const stripePrice = product.prices.find((price) => price.provider === 'stripe');
  const fromPrice = stripePrice?.creditsIncludedMinor;
  const fromMetadata = Number(product.metadata?.creditsIncludedMinor || product.metadata?.creditAmountMinor || 0);
  return Number(fromPrice || fromMetadata || 0);
}

function formatDate(value?: string): string {
  if (!value) return 'never';
  return new Date(value).toLocaleString();
}

/** True when the access snapshot grants `capability` (supports `x.*` wildcards). */
function snapshotAllows(capabilities: Record<string, { allowed?: boolean }> | undefined, capability: string): boolean {
  return Object.entries(capabilities || {}).some(([granted, value]) => {
    if (!value?.allowed) return false;
    if (granted === capability) return true;
    return granted.endsWith('.*') && capability.startsWith(granted.slice(0, -1));
  });
}

export default function ProjectBillingPage(): React.ReactElement {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const service = React.useMemo(() => createBillingAccessApiService(), []);
  const { account, access, loading, error, refresh, recompute } = useBillingAccess(projectId);
  const [subscriptionProducts, setSubscriptionProducts] = React.useState<BillingProduct[]>([]);
  const [creditPacks, setCreditPacks] = React.useState<BillingProduct[]>([]);
  const [ledger, setLedger] = React.useState<BillingCreditLedgerEntry[]>([]);
  const [packsLoading, setPacksLoading] = React.useState(false);
  const [packsError, setPacksError] = React.useState<string | null>(null);
  const [checkoutPackProductId, setCheckoutPackProductId] = React.useState<string | null>(null);
  const [creditHistoryOpen, setCreditHistoryOpen] = React.useState(false);
  const [selectedLedgerTaskId, setSelectedLedgerTaskId] = React.useState<string | null>(null);
  const [subscribingProductId, setSubscribingProductId] = React.useState<string | null>(null);
  const [subscriptionActionId, setSubscriptionActionId] = React.useState<string | null>(null);
  const [subscriptionActionError, setSubscriptionActionError] = React.useState<string | null>(null);
  const [offerError, setOfferError] = React.useState<string | null>(null);
  const allTasks = useTasks({ statusMode: 'all', sortBy: 'updatedAt', sortDirection: 'desc' });
  const recentLedger = React.useMemo(() => ledger.slice(0, 5), [ledger]);
  const billedTasks = React.useMemo(
    () => allTasks.filter((task) => getTaskBilling(task)).slice(0, 10),
    [allTasks],
  );
  const activeProductIds = React.useMemo(
    () => (access?.activeSubscriptions || []).map((subscription) => subscription.productId),
    [access?.activeSubscriptions],
  );
  const needsActiveSubscriptionDetails = React.useMemo(
    () => (access?.activeSubscriptions || []).some((subscription) =>
      (subscription.status === 'active' || subscription.status === 'trialing')
      && subscription.fundingSource === undefined
    ),
    [access?.activeSubscriptions],
  );

  const loadCreditData = React.useCallback(async () => {
    if (!projectId) return;
    setPacksLoading(true);
    setPacksError(null);
    try {
      const [nextPacks, nextLedger] = await Promise.all([
        service.listCreditPacks(projectId),
        service.listCreditLedger(projectId, 500),
      ]);
      const nextSubscriptionProducts = await service.listSubscriptionProducts(projectId);
      setSubscriptionProducts(nextSubscriptionProducts);
      setCreditPacks(nextPacks);
      setLedger(nextLedger);
    } catch (err) {
      setPacksError((err as Error).message);
    } finally {
      setPacksLoading(false);
    }
  }, [projectId, service]);

  const refreshBillingData = React.useCallback(async () => {
    await Promise.all([
      refresh(true),
      loadCreditData(),
    ]);
  }, [loadCreditData, refresh]);

  React.useEffect(() => {
    refreshBillingData();
  }, [refreshBillingData]);

  const activeSubscriptionDetailsRefreshAttempted = React.useRef(false);
  React.useEffect(() => {
    if (
      !projectId
      || loading
      || !needsActiveSubscriptionDetails
      || activeSubscriptionDetailsRefreshAttempted.current
    ) {
      return;
    }
    activeSubscriptionDetailsRefreshAttempted.current = true;
    void (async () => {
      try {
        await recompute();
        await loadCreditData();
      } catch (err) {
        setPacksError((err as Error).message);
      }
    })();
  }, [loadCreditData, loading, needsActiveSubscriptionDetails, projectId, recompute]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('billingCheckout') === 'success') {
      refreshBillingData();
    }
  }, [location.search, refreshBillingData]);

  const startCheckout = React.useCallback(async (productId: string) => {
    if (!projectId) return;
    setCheckoutPackProductId(productId);
    setPacksError(null);
    try {
      const session = await service.createCreditPackCheckoutSession(projectId, productId);
      if (!session.url) {
        throw new Error('Stripe checkout did not return a redirect URL');
      }
      window.location.assign(session.url);
    } catch (err) {
      setPacksError((err as Error).message);
    } finally {
      setCheckoutPackProductId(null);
    }
  }, [projectId, service]);

  const handleRecompute = React.useCallback(async () => {
    await recompute();
    await loadCreditData();
  }, [loadCreditData, recompute]);

  const handleSubscribe = React.useCallback(async (productId: string) => {
    if (!projectId) return;
    setSubscribingProductId(productId);
    setOfferError(null);
    try {
      const session = await service.createSubscriptionCheckoutSession(projectId, productId);
      if (session?.url) {
        window.location.assign(session.url);
      } else {
        setSubscribingProductId(null);
        setOfferError('Could not start checkout — no session URL returned.');
      }
    } catch (err) {
      setSubscribingProductId(null);
      setOfferError((err as Error).message);
    }
  }, [projectId, service]);

  const handleCancelRenewal = React.useCallback(async (subscription: BillingSubscriptionSummary) => {
    if (!projectId) return;
    const confirmed = window.confirm(
      `Cancel renewal for ${subscription.productName}? Access remains active until ${formatDate(subscription.currentPeriodEnd)}.`,
    );
    if (!confirmed) return;
    setSubscriptionActionId(subscription.id);
    setSubscriptionActionError(null);
    try {
      await service.cancelSubscription(projectId, subscription.id);
      await refreshBillingData();
    } catch (err) {
      setSubscriptionActionError((err as Error).message);
    } finally {
      setSubscriptionActionId(null);
    }
  }, [projectId, refreshBillingData, service]);

  const handleReactivateSubscription = React.useCallback(async (subscription: BillingSubscriptionSummary) => {
    if (!projectId) return;
    setSubscriptionActionId(subscription.id);
    setSubscriptionActionError(null);
    try {
      await service.reactivateSubscription(projectId, subscription.id);
      await refreshBillingData();
    } catch (err) {
      setSubscriptionActionError((err as Error).message);
    } finally {
      setSubscriptionActionId(null);
    }
  }, [projectId, refreshBillingData, service]);

  // After returning from Stripe Checkout (?billingCheckout=success) the
  // subscription is activated ASYNCHRONOUSLY by the webhook. Poll a recompute a
  // few times so the page unblocks + shows the subscription on its own, without
  // requiring a manual "Recompute access" click.
  const accessRef = React.useRef(access);
  React.useEffect(() => { accessRef.current = access; }, [access]);
  const checkoutParams = new URLSearchParams(location.search);
  const checkoutSuccess = checkoutParams.get('billingCheckout') === 'success';
  const checkoutSubscriptionProductId = checkoutParams.get('productId');
  const activeProductIdsRef = React.useRef(activeProductIds);
  React.useEffect(() => { activeProductIdsRef.current = activeProductIds; }, [activeProductIds]);
  const clearCheckoutParams = React.useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete('billingCheckout');
    params.delete('session_id');
    params.delete('productId');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate]);
  React.useEffect(() => {
    if (!checkoutSuccess || !projectId) return undefined;
    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async (): Promise<void> => {
      if (cancelled) return;
      attempt += 1;
      try { await recompute(); } catch { /* transient — will retry */ }
      if (cancelled) return;
      const active = checkoutSubscriptionProductId
        ? activeProductIdsRef.current.includes(checkoutSubscriptionProductId)
        : (accessRef.current?.activeSubscriptions || []).length > 0
          || snapshotAllows(accessRef.current?.capabilities, 'web_onboarding.access');
      if (active) {
        clearCheckoutParams();
        return;
      }
      if (!active && attempt < 6) {
        timer = setTimeout(() => { void poll(); }, 2500);
      }
    };
    timer = setTimeout(() => { void poll(); }, 1200);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [checkoutSubscriptionProductId, checkoutSuccess, clearCheckoutParams, projectId, recompute]);

  // The impression subscription grants `web_onboarding.access`; when it's absent
  // the project has no active impression plan → show the paywall offer on top.
  const needsSubscription = !loading && !snapshotAllows(access?.capabilities, 'web_onboarding.access');
  const showSelfServeSubscriptions = !needsSubscription && subscriptionProducts.length > 0;

  if (!projectId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Project ID is required.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1180 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5">Billing</Typography>
          <Typography variant="body2" color="text.secondary">
            Project subscription access, trial state, credits, and usage quotas.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={handleRecompute} disabled={loading || packsLoading}>
          Recompute access
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {packsError && <Alert severity="error" sx={{ mb: 2 }}>{packsError}</Alert>}
      {new URLSearchParams(location.search).get('billingCheckout') === 'cancelled' && (
        <Alert severity="info" sx={{ mb: 2 }}>Credit purchase was cancelled.</Alert>
      )}
      {checkoutSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} data-testid="billing-checkout-success">
          Payment received — activating access. This page refreshes automatically once Stripe confirms (usually a few seconds).
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {needsSubscription && (
            <Paper
              elevation={0}
              sx={{ p: 3, borderRadius: 1, border: 2, borderColor: 'primary.main', bgcolor: 'action.hover' }}
              data-testid="billing-paywall-offer"
            >
              <Stack spacing={1.5}>
                <Typography variant="h6">Subscribe to unlock this project</Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose the subscription surface this project needs.
                </Typography>
                {offerError && <Alert severity="error">{offerError}</Alert>}
                <SubscriptionProductsList
                  products={subscriptionProducts}
                  loading={packsLoading}
                  busyProductId={subscribingProductId}
                  activeProductIds={activeProductIds}
                  actionLabel="Subscribe"
                  onSubscribe={handleSubscribe}
                />
              </Stack>
            </Paper>
          )}

          {showSelfServeSubscriptions && (
            <Paper
              variant="outlined"
              sx={{ p: 2, borderRadius: 1 }}
              data-testid="billing-self-serve-subscriptions"
            >
              <Stack spacing={1.5}>
                <Typography variant="h6">Available subscriptions</Typography>
                <Typography variant="body2" color="text.secondary">
                  Add another subscription surface to this project.
                </Typography>
                {offerError && <Alert severity="error">{offerError}</Alert>}
                <SubscriptionProductsList
                  products={subscriptionProducts}
                  loading={packsLoading}
                  busyProductId={subscribingProductId}
                  activeProductIds={activeProductIds}
                  actionLabel="Subscribe"
                  onSubscribe={handleSubscribe}
                />
              </Stack>
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6">Access status</Typography>
                <Typography variant="body2" color="text.secondary">
                  Updated {access?.updatedAt ? new Date(access.updatedAt).toLocaleString() : 'never'}
                </Typography>
              </Box>
              <Chip label={access?.status || 'blocked'} color={access?.status === 'active' ? 'success' : 'default'} />
                <Chip
                  label={account
                  ? `${moneyToLabel(account.creditBalance.availableMinor, account.creditBalance.currency)} credits`
                  : 'No credits'}
                variant="outlined"
              />
              {account?.creditBalance.reservedMinor ? (
                <Chip
                  label={`${moneyToLabel(account.creditBalance.reservedMinor, account.creditBalance.currency)} reserved`}
                  variant="outlined"
                  color="warning"
                />
              ) : null}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'flex-start' }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6">AI credits</Typography>
                <Typography variant="body2" color="text.secondary">
                  Credits cover paid AI overage after included AI token quota is exhausted.
                </Typography>
              </Box>
              <Chip
                label={account
                  ? `${moneyToLabel(
                    account.creditBalance.availableMinor - (account.creditBalance.reservedMinor || 0),
                    account.creditBalance.currency,
                  )} spendable`
                  : 'No balance'}
                color="primary"
                variant="outlined"
              />
            </Stack>
            <Divider sx={{ my: 2 }} />
            {packsLoading ? (
              <Box sx={{ py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Stack spacing={1}>
                {creditPacks.map((pack) => {
                  const price = pack.prices.find((candidate) => candidate.provider === 'stripe');
                  const creditAmount = getCreditAmount(pack);
                  return (
                    <Stack
                      key={pack.id}
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'stretch', md: 'center' }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography>{pack.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {pack.description || `${moneyToLabel(creditAmount, price?.currency || account?.currency || 'usd')} Segmently credits`}
                        </Typography>
                      </Box>
                      <Chip size="small" label={`${moneyToLabel(creditAmount, price?.currency || account?.currency || 'usd')} credits`} />
                      <Button
                        variant="contained"
                        startIcon={<ShoppingCartIcon />}
                        disabled={!price?.stripePriceId || checkoutPackProductId === pack.id}
                        onClick={() => startCheckout(pack.id)}
                      >
                        Buy credits
                      </Button>
                    </Stack>
                  );
                })}
                {creditPacks.length === 0 && (
                  <Typography variant="body2" color="text.secondary">No self-serve credit packs are configured.</Typography>
                )}
              </Stack>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Active subscriptions</Typography>
            <Stack spacing={1}>
              {subscriptionActionError && <Alert severity="error">{subscriptionActionError}</Alert>}
              {(access?.activeSubscriptions || []).map((subscription) => {
                const canSelfServe = subscription.fundingSource === 'stripe'
                  && Boolean(subscription.providerSubscriptionId)
                  && (subscription.status === 'active' || subscription.status === 'trialing');
                const isScheduledCancel = Boolean(subscription.cancelAtPeriodEnd);
                const busy = subscriptionActionId === subscription.id;
                return (
                  <Stack
                    key={subscription.id}
                    data-testid="billing-active-subscription"
                    data-product-id={subscription.productId}
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography>{subscription.productName}</Typography>
                      {isScheduledCancel && (
                        <Typography variant="caption" color="text.secondary">
                          Access remains until {formatDate(subscription.currentPeriodEnd)}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      size="small"
                      label={isScheduledCancel ? 'cancelled at period end' : subscription.status}
                      color={isScheduledCancel ? 'warning' : 'default'}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {subscription.productId}
                    </Typography>
                    {canSelfServe && (
                      <Button
                        size="small"
                        variant={isScheduledCancel ? 'contained' : 'outlined'}
                        startIcon={isScheduledCancel ? <ReplayIcon /> : <EventBusyIcon />}
                        disabled={busy}
                        onClick={() => {
                          if (isScheduledCancel) {
                            void handleReactivateSubscription(subscription);
                          } else {
                            void handleCancelRenewal(subscription);
                          }
                        }}
                      >
                        {isScheduledCancel ? 'Reactivate' : 'Cancel renewal'}
                      </Button>
                    )}
                  </Stack>
                );
              })}
              {(access?.activeSubscriptions || []).length === 0 && (
                <Alert severity="info" data-testid="billing-no-subscription">No active Segmently platform subscription is attached to this project.</Alert>
              )}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Capabilities</Typography>
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
              {Object.keys(access?.capabilities || {}).map((capability) => (
                <Chip key={capability} size="small" label={capability} color="success" variant="outlined" />
              ))}
              {Object.keys(access?.capabilities || {}).length === 0 && (
                <Typography variant="body2" color="text.secondary">No capabilities granted.</Typography>
              )}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Usage quotas</Typography>
            <Stack spacing={1}>
              {Object.values(access?.quotas || {}).map((quota) => (
                <Stack key={quota.meterId} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <Typography sx={{ flex: 1 }}>{quota.meterId}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {quota.used} used / {quota.limit} included
                  </Typography>
                  <Chip size="small" label={`${quota.remaining} remaining`} />
                </Stack>
              ))}
              {Object.values(access?.quotas || {}).length === 0 && (
                <Typography variant="body2" color="text.secondary">No usage quotas configured.</Typography>
              )}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 1 }}>
              <Typography variant="h6" sx={{ flex: 1 }}>Recent credit ledger</Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => setCreditHistoryOpen(true)}
              >
                View history
              </Button>
            </Stack>
            <Stack spacing={1}>
              {recentLedger.map((entry) => (
                <Stack
                  key={entry.id}
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', md: 'center' }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography>{entry.source}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(entry.createdAt)}{entry.sourceId ? ` · ${entry.sourceId}` : ''}
                    </Typography>
                  </Box>
                  <Chip size="small" label={entry.type} />
                  <Typography
                    sx={{ minWidth: 120, textAlign: { xs: 'left', md: 'right' } }}
                    color={entry.deltaMinor < 0 ? 'error.main' : 'success.main'}
                  >
                    {entry.deltaMinor < 0 ? '-' : '+'}
                    {moneyToLabel(Math.abs(entry.deltaMinor), entry.currency)}
                  </Typography>
                </Stack>
              ))}
              {recentLedger.length === 0 && (
                <Typography variant="body2" color="text.secondary">No credit ledger entries yet.</Typography>
              )}
              {ledger.length > recentLedger.length && (
                <Typography variant="caption" color="text.secondary">
                  Showing {recentLedger.length} of {ledger.length} entries. Open history to filter by date and operation.
                </Typography>
              )}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Recent AI task billing</Typography>
            <Stack spacing={1}>
              {billedTasks.map((task) => {
                const billing = getTaskBilling(task);
                if (!billing) return null;
                const currency = billing.currency || account?.currency || 'usd';
                return (
                  <Stack
                    key={task.id}
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography>{task.action || task.taskType}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Started by {task.userId || 'unknown'} · {billing.provider || task.provider || 'provider'}:{billing.model || task.model || 'model'}
                      </Typography>
                    </Box>
                    <Chip size="small" label={task.status} />
                    <Typography variant="caption" color="text.secondary">
                      {billing.quotaQuantity || 0} quota / {billing.creditQuantity || 0} billed tokens
                    </Typography>
                    <Typography
                      sx={{ minWidth: 130, textAlign: { xs: 'left', md: 'right' } }}
                      color={(billing.creditDebitMinor || 0) > 0 ? 'error.main' : 'text.secondary'}
                    >
                      {moneyToLabel(billing.creditDebitMinor || 0, currency)} credits
                    </Typography>
                  </Stack>
                );
              })}
              {billedTasks.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No AI task billing has been recorded for this project yet.
                </Typography>
              )}
            </Stack>
          </Paper>
        </Stack>
      )}
      <CreditLedgerHistoryDialog
        open={creditHistoryOpen}
        onClose={() => setCreditHistoryOpen(false)}
        ledger={ledger}
        fallbackCurrency={account?.currency || 'usd'}
        loading={packsLoading}
        onRefresh={loadCreditData}
        onOpenBackendTask={(taskId) => setSelectedLedgerTaskId(taskId)}
      />
      <TaskDetailDialog
        open={Boolean(selectedLedgerTaskId)}
        taskId={selectedLedgerTaskId}
        onClose={() => setSelectedLedgerTaskId(null)}
      />
    </Box>
  );
}
